/* A sub's documents for one job (Duncan 2026-10-22, from the construction administration call): "for each job,
   we require a certificate of insurance" -- naming the job, with what E3 asks for on it -- and "a job site safety
   plan ... specific to the job". Here: the window that adds or edits one -- adding a certificate, Summit reads it (a
   PDF, a scan or a phone photo) and looks for what's asked by keyword (Duncan: "doesn't have to match exactly"),
   ticking what it finds and filling the dates -- the COI wording's Copy, and the wording's own window. The rows
   that list them are SubDocRows.js. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../ui';
import { ModalShell, Field, Label, ErrorBanner, PrimaryButton, SecondaryButton, label as microLabel } from './SubUi';
import { saveCoiWording, scanJobCoi } from '../../api';
import { jobDocKindOf, COI_CHECKS, DATE_LABEL, fillCoiWording, fmtSize, centsFromText, moneyOfCents } from '../../lib/subcontractors';

const MAX_UPLOAD_BYTES = 150 * 1024 * 1024; // as the server (Duncan 2026-10-24: a 63 MB safety manual)
const DAY_RE = /^(19|20)\d{2}-\d{2}-\d{2}$/;
const muted = { fontSize: 13, color: 'var(--text-3)' };
/** A check's words for this job: "the owner" becomes its name. */
const checkLabel = (c, owner) => c.label.replace('the owner', owner || 'the owner');

const smallBtn = { height: 28, padding: '0 10px', fontSize: 13 };

/** Copies the COI wording with this job's owner, name and number filled in. Where the browser won't let a page
    copy, it shows the wording, selected, to copy by hand. */
export function CopyWording({ wording, job }) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(false);
  const text = fillCoiWording(wording, job);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      setShown(true);
    }
  };
  return (
    <>
      <SecondaryButton onClick={copy} title={text} aria-label={copied ? 'Copied' : 'Copy COI wording'} style={smallBtn}>
        {copied ? <><Icon name="check" size={13} stroke={2} />Copied</> : 'Copy COI wording'}
      </SecondaryButton>
      {shown && (
        <ModalShell title="COI wording" subtitle={`For ${job.name}. Copy it (Ctrl+C or ⌘C) and send it to the sub.`} onClose={() => setShown(false)} width={560}
          footer={<SecondaryButton onClick={() => setShown(false)}>Done</SecondaryButton>}>
          <textarea readOnly value={text} aria-label="COI wording for this job" ref={(el) => { if (el) el.select(); }}
            style={{ width: '100%', minHeight: 120, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-surface-2)', color: 'var(--text-1)', fontFamily: 'var(--font-sans)', fontSize: 14, lineHeight: 1.5, boxSizing: 'border-box', resize: 'vertical' }} />
        </ModalShell>
      )}
    </>
  );
}

const SCAN_SAYS = {
  off: 'Summit can’t read certificates yet. Tick what this one shows.',
  unreadable: 'Summit couldn’t read this file (too blurry, or no text on it). Tick what it shows.',
  failed: 'Reading the certificate didn’t work this time. Tick what it shows, or choose the file again.',
};

/** What Summit read off a certificate, check by check: found (with the words it found) or not. */
function ScanFindings({ scan, owner }) {
  if (scan.status !== 'read') {
    return <div role="status" style={{ ...muted, padding: '9px 12px', borderRadius: 8, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{SCAN_SAYS[scan.status] || SCAN_SAYS.failed}</div>;
  }
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
      <div style={{ ...microLabel, fontSize: 10.5, marginBottom: 6 }}>What Summit found on the certificate</div>
      <div style={{ display: 'grid', gridTemplateColumns: '16px minmax(0, 1fr)', gap: '4px 6px', alignItems: 'start', fontSize: 13, lineHeight: 1.4 }}>
        {COI_CHECKS.map((c) => {
          const quote = scan.found && scan.found[c.key];
          // Nearly there: additional insured wording that doesn't name E3 and the owner (Duncan 2026-10-22).
          const note = !quote && scan.notes && scan.notes[c.key];
          const shown = quote || (note && note.quote);
          const tone = quote ? 'var(--success)' : c.key === 'names_job' ? 'var(--danger)' : 'var(--warning)';
          return (
            <React.Fragment key={c.key}>
              <Icon name={quote ? 'check' : 'alert-triangle'} size={13} stroke={2} style={{ color: tone, marginTop: 2 }} />
              <span style={{ minWidth: 0, color: quote ? 'var(--text-1)' : tone }}>
                {checkLabel(c, owner)}{quote ? '' : note ? ` — ${note.text}` : ' — not found'}
                {shown && <span title={shown} style={{ display: 'block', color: 'var(--text-3)', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>“{shown.replace(/^…|…$/g, '')}”</span>}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <div style={{ ...muted, fontSize: 12, marginTop: 6 }}>The form’s standard fine print, which mentions additional insured and subrogation on every certificate, isn’t counted.</div>
    </div>
  );
}

/** Adds a job document (the file, then its details) or edits one's details. A certificate is read as soon as it's
    chosen: what Summit finds is ticked and its policy dates filled in, to correct before saving. A subcontract
    (2026-10-29) goes in with its amount -- `contract`, the one on file -- its CE Sub # and date from CE's list for the
    job (`ceSubcontracts`) when CE has exactly one. */
export function JobDocDialog({ mode, kind, doc, job, projectId, subName, replacing, initialFile, contract = null, ceSubcontracts = [], onClose, onSubmit }) {
  const isEdit = mode === 'edit';
  const type = jobDocKindOf(isEdit ? doc.doc_type : kind);
  const coi = type.value === 'insurance';
  const subcontract = type.value === 'subcontract';
  const fromCe = subcontract && !isEdit && !replacing && ceSubcontracts.length === 1 ? ceSubcontracts[0] : null;
  const was = isEdit ? doc : replacing;
  const [form, setForm] = useState(() => ({
    title: was?.title && was.title !== type.title ? was.title : '',
    number: was?.number || (fromCe && fromCe.number) || '',
    signed_date: was?.signed_date || (fromCe && fromCe.contract_date ? String(fromCe.contract_date).slice(0, 10) : ''),
    effective_date: doc?.effective_date || '',
    expiry_date: doc?.expiry_date || '',
    amount: contract ? moneyOfCents(contract.amount_cents) : '',
    ...Object.fromEntries(COI_CHECKS.map((c) => [c.key, !!doc?.[c.key]])),
  }));
  const [file, setFile] = useState(null);
  const [scan, setScan] = useState(() => (isEdit && doc.scan) || null); // what Summit read off the certificate
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const readFor = useRef(null); // the file being read: a newer choice wins
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const choose = useCallback(async (f) => {
    setFile(f);
    setScan(null);
    if (!f || !coi || !projectId) return;
    readFor.current = f;
    setReading(true);
    try {
      const r = await scanJobCoi(projectId, f);
      if (readFor.current !== f) return;
      setScan(r);
      if (r.status === 'read') {
        setForm((cur) => ({
          ...cur,
          ...Object.fromEntries(COI_CHECKS.map((c) => [c.key, !!(r.checks && r.checks[c.key])])),
          effective_date: (r.dates && r.dates.effective) || cur.effective_date,
          expiry_date: (r.dates && r.dates.expiry) || cur.expiry_date,
        }));
      }
    } catch (e) {
      if (readFor.current === f) setScan({ status: 'failed', found: {}, dates: {} });
    } finally {
      if (readFor.current === f) setReading(false);
    }
  }, [coi, projectId]);
  // A file dropped on (or chosen from) a card's upload area comes in already chosen (2026-10-23).
  const started = useRef(false);
  useEffect(() => {
    if (!initialFile || started.current) return;
    started.current = true;
    choose(initialFile);
  }, [initialFile, choose]);

  const submit = async () => {
    if (busy || reading) return;
    if (!isEdit && !file) return setError('Choose the file.');
    if (file && file.size > MAX_UPLOAD_BYTES) return setError('The file is larger than the 150MB limit.');
    if (coi) {
      if (!DAY_RE.test(form.effective_date) || !DAY_RE.test(form.expiry_date)) return setError('A certificate of insurance needs its effective and expiration dates.');
      if (form.expiry_date < form.effective_date) return setError('It expires before it takes effect: check the dates.');
    }
    const cents = subcontract ? centsFromText(form.amount) : null;
    if (subcontract && !(cents > 0)) return setError('Type the subcontract’s amount, as on the executed subcontract.');
    if (subcontract && form.signed_date && !DAY_RE.test(form.signed_date)) return setError('The signed date isn’t a date.');
    const details = {
      doc_type: type.value,
      title: form.title.trim(),
      ...(coi ? {
        number: form.number.trim(),
        effective_date: form.effective_date,
        expiry_date: form.expiry_date,
        ...Object.fromEntries(COI_CHECKS.map((c) => [c.key, form[c.key]])),
      } : {}),
      ...(subcontract ? { number: form.number.trim(), signed_date: form.signed_date, amount_cents: String(cents) } : {}),
    };
    setError(null);
    setBusy(true);
    try {
      await onSubmit(details, file, !isEdit && coi ? scan : null);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? `Edit ${type.label.toLowerCase()}` : `${replacing ? 'Replace' : 'Add'} ${type.label.toLowerCase()}`}
      subtitle={`${subName} · ${job.name}${job.number ? ` (#${job.number})` : ''}${coi && job.owner ? ` · owner ${job.owner}` : ''}${replacing ? ` · replaces ${replacing.filename}, kept in storage` : ''}`}
      onClose={onClose} busy={busy} width={560}
      footer={(
        <>
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy || reading} onClick={submit}>{reading ? 'Reading…' : busy ? (isEdit ? 'Saving…' : 'Uploading…') : isEdit ? 'Save changes' : `${replacing ? 'Replace' : 'Add'} ${type.short}`}</PrimaryButton>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        {!isEdit && (
          <div>
            <Label required>File</Label>
            <input ref={fileRef} type="file" accept={coi ? 'application/pdf,image/*' : undefined} style={{ display: 'none' }} onChange={(e) => choose(e.target.files?.[0] || null)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <SecondaryButton onClick={() => fileRef.current?.click()}><Icon name="upload" size={14} />{file ? 'Choose another' : 'Choose file'}</SecondaryButton>
              <span style={{ ...muted, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file ? `${file.name} · ${fmtSize(file.size)}` : coi ? 'A PDF, a scan or a photo of it, up to 150MB' : 'A PDF or a photo of it, up to 150MB'}</span>
            </div>
          </div>
        )}
        {coi && reading && <div role="status" style={{ ...muted, padding: '9px 12px', borderRadius: 8, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>Reading the certificate…</div>}
        {coi && !reading && scan && <ScanFindings scan={scan} owner={job.owner} />}
        {coi && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              {['effective_date', 'expiry_date'].map((k) => (
                <Field key={k} id={`jobdoc-${k}`} type="date" label={DATE_LABEL[k]} required value={form[k]} onChange={set(k)}
                  hint={scan && scan.status === 'read' && scan.dates && scan.dates[k === 'effective_date' ? 'effective' : 'expiry'] ? 'read from it' : undefined} />
              ))}
            </div>
            <div style={{ ...muted, marginTop: -6 }}>Expires: the soonest expiry on the certificate.</div>
            <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <legend style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6, padding: 0 }}>
                What the certificate shows{scan && scan.status === 'read' ? ' (ticked from what Summit found; correct anything it got wrong)' : ''}
              </legend>
              {COI_CHECKS.map((c) => (
                <label key={c.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 14, color: 'var(--text-1)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form[c.key]} onChange={(e) => set(c.key)(e.target.checked)} style={{ margin: '3px 0 0' }} />
                  <span>
                    {checkLabel(c, job.owner)}
                    {c.hint && <span style={{ display: 'block', ...muted, fontSize: 12.5 }}>{c.hint.replace('the owner', job.owner || 'the owner')}</span>}
                  </span>
                </label>
              ))}
              <div style={{ ...muted, fontSize: 12.5 }}>A certificate that doesn’t name the job doesn’t count for it. The rest are followed up when they’re missing.</div>
            </fieldset>
            <Field id="jobdoc-number" label="Policy #" value={form.number} onChange={set('number')} />
          </>
        )}
        {subcontract && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
              <Field id="jobdoc-amount" label="Amount" required value={form.amount} onChange={set('amount')} placeholder="$0.00"
                inputMode="decimal" onBlur={() => { const c = centsFromText(form.amount); if (c != null) set('amount')(moneyOfCents(c)); }}
                hint="as on the executed subcontract" />
              <Field id="jobdoc-signed" type="date" label={DATE_LABEL.signed_date} value={form.signed_date} onChange={set('signed_date')}
                hint={fromCe && fromCe.contract_date ? 'from CE' : undefined} />
            </div>
            <Field id="jobdoc-number" label="CE Sub #" value={form.number} onChange={set('number')} hint={fromCe ? 'from CE for this job' : 'optional'} />
            <div style={{ ...muted, marginTop: -6 }}>The sub’s Billing tab builds its schedule of values on this amount.</div>
          </>
        )}
        <Field id="jobdoc-title" label="Title" hint="optional" value={form.title} onChange={set('title')} placeholder={type.title} />
      </div>
    </ModalShell>
  );
}

/** The paragraph a sub's insurance agent puts in a job's COI description box, with {owner}, {job} and {number}
    where the project tab fills in the job's owner, name and number. Subcontractor Mgmt writes it. */
export function CoiWordingDialog({ initial, onClose, onSaved }) {
  const [text, setText] = useState(initial || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const example = { owner: 'Bryan ISD', name: 'Bluebonnet Elementary Lighting', number: '26014-L-01' };
  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await saveCoiWording(text);
      onSaved(r.wording);
    } catch (e) {
      setError(e.message || 'Failed to save the COI wording');
      setBusy(false);
    }
  };
  return (
    <ModalShell title="COI wording" subtitle="What subs’ insurance agents put in the description box of a job’s certificate. Each project’s Subcontractors tab copies it with that job filled in."
      onClose={onClose} busy={busy} width={600}
      footer={(
        <>
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={save}>{busy ? 'Saving…' : 'Save wording'}</PrimaryButton>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <Field id="coi-wording" as="textarea" label="Wording" value={text} onChange={setText} style={{ minHeight: 140 }}
          placeholder="E3 Entegral Solutions and {owner} are named as additional insured … {job} …" />
        <div style={muted}>Write <span className="mono">{'{owner}'}</span>, <span className="mono">{'{job}'}</span> and <span className="mono">{'{number}'}</span> where the job’s owner, name and number go.</div>
        {text.trim() && (
          <div>
            <Label>Example, for {example.name}</Label>
            <div style={{ padding: '9px 12px', borderRadius: 8, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-2)', whiteSpace: 'pre-wrap' }}>
              {fillCoiWording(text, example)}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

